import { useState, useEffect, useRef } from "react";

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Về Olympic Academy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tổ chức giáo dục hàng đầu Việt Nam với sứ mệnh khai phóng tiềm
              năng và dẫn dắt thế hệ tương lai trong kỷ nguyên số, theo tinh
              thần Olympic: Xuất sắc - Tôn trọng - Tình bạn
            </p>
          </div>

          <div
            ref={sectionRef}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16"
          >
            <div
              className={`transform transition-all duration-1000 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              }`}
            >
              <img
                src="/TamNhinVaSuMenh.jpg"
                alt="Lớp học tại Olympic Academy"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
            <div
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Tầm nhìn & Sứ mệnh
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                <strong className="text-blue-600">Tầm nhìn:</strong> Xây dựng
                Olympic Academy trở thành tổ chức giáo dục hàng đầu Việt Nam,
                nơi mỗi học sinh được phát huy tối đa tiềm năng để chinh phục
                các đỉnh cao thành tích trong các kỳ thi Olympic quốc gia và
                quốc tế.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                <strong className="text-green-600">Sứ mệnh:</strong> Khai phóng
                tiềm năng và dẫn dắt thế hệ tương lai Việt Nam trong kỷ nguyên
                số thông qua việc đào tạo chuyên sâu các tài năng trẻ trong Toán
                học, Lập trình và Trí tuệ Nhân tạo (AI).
              </p>
              <div className="space-y-4">
                <div
                  className={`flex items-start space-x-3 transform transition-all duration-700 delay-500 ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                >
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-global-line text-blue-600 text-sm"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">
                      Kết nối tri thức toàn cầu:
                    </strong>
                    <span className="text-gray-600">
                      {" "}
                      Tương tự năm vòng tròn Olympic, chúng tôi kết nối tri thức
                      quốc tế
                    </span>
                  </div>
                </div>
                <div
                  className={`flex items-start space-x-3 transform transition-all duration-700 delay-700 ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                >
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-star-line text-green-600 text-sm"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">
                      Phát triển tài năng rực rỡ:
                    </strong>
                    <span className="text-gray-600">
                      {" "}
                      Như ngôi sao năm cánh, dẫn dắt học viên trở thành những
                      ngôi sao sáng
                    </span>
                  </div>
                </div>
                <div
                  className={`flex items-start space-x-3 transform transition-all duration-700 delay-900 ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                >
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-trophy-line text-yellow-600 text-sm"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">
                      Tinh thần Olympic:
                    </strong>
                    <span className="text-gray-600">
                      {" "}
                      Xuất sắc, Tôn trọng và Tình bạn trong mọi hoạt động học
                      tập
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div
              className={`text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl transform transition-all duration-700 delay-200 ${
                isVisible
                  ? "translate-y-0 opacity-100 rotate-0"
                  : "translate-y-12 opacity-0 -rotate-12"
              }`}
            >
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Thành tích quốc tế
              </h4>
              <p className="text-gray-600">
                Hơn 50 giải thưởng Olympic quốc gia và quốc tế
              </p>
            </div>

            <div
              className={`text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl transform transition-all duration-700 delay-400 ${
                isVisible
                  ? "translate-y-0 opacity-100 rotate-0"
                  : "translate-y-12 opacity-0 rotate-12"
              }`}
            >
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-cpu-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Chuyên sâu AI
              </h4>
              <p className="text-gray-600">
                Đào tạo Toán học, Lập trình và Trí tuệ Nhân tạo
              </p>
            </div>

            <div
              className={`text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl transform transition-all duration-700 delay-600 ${
                isVisible
                  ? "translate-y-0 opacity-100 rotate-0"
                  : "translate-y-12 opacity-0 -rotate-12"
              }`}
            >
              <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Giảng viên xuất sắc
              </h4>
              <p className="text-gray-600">
                Đội ngũ chuyên gia hàng đầu trong từng lĩnh vực
              </p>
            </div>

            <div
              className={`text-center p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl transform transition-all duration-700 delay-800 ${
                isVisible
                  ? "translate-y-0 opacity-100 rotate-0"
                  : "translate-y-12 opacity-0 rotate-12"
              }`}
            >
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Tầm nhìn toàn cầu
              </h4>
              <p className="text-gray-600">
                Chuẩn bị học sinh cạnh tranh trên đấu trường quốc tế
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
